import React, { useState, useEffect } from 'react';
import ProjectApi from '../project/projectApi';
import { Project } from '../project/projectType';
import { Customer } from '../customer/customerType';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchProjectType } from '../reference/referenceApi';
import { getSessionData } from '../lib/global_function'
import CustomerApi from '../customer/customerApi';
import { FilePlus } from 'lucide-react';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select } from '../components/ui/select';

const CreateProjectPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Create', path: '/main/project/create' },
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const { customer } = location.state || {};

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [projectTypeOptions, setProjectTypeOptions] = useState<string[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [dateError, setDateError] = useState<string>('');

  const [project, setProject] = useState<Partial<Project>>({
    name: '',
    project_type: '',
    estimated_sum: 0,
    estimated_duration_days: 0,
    company_name: '',
    customer_id: '',
    created_by_account_id: '',
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    if (customer && customer.customer_id) {
      setSelectedCustomerId(customer.customer_id);
    }
  }, [customer]);

  useEffect(() => {
    const sessionData = getSessionData(process.env.REACT_APP_SESSION_STORAGE_KEY as string);
    const companyName = String(sessionData?.company_name);
    const accountId = String(sessionData?.account_id);

    if (typeof companyName === 'string' && companyName.trim() && typeof accountId === 'string' && accountId.trim()) {
      setErrorMessage('');
      setProject(prevState => ({
        ...prevState,
        company_name: companyName,
        created_by_account_id: accountId,
      }));
    } else {
      setErrorMessage('You must create a company first');
    }
  }, []);

  useEffect(() => {
    const fetchCustomer = async () => {
      setErrorMessage('');
      setMessage('');
      try {
        const fetchedCustomers = await CustomerApi.get();
        setCustomers(fetchedCustomers);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      }
    };
    fetchCustomer();
  }, []);

  useEffect(() => {
    const loadProjectType = async () => {
      try {
        const projectTypeData = await fetchProjectType();
        setProjectTypeOptions(projectTypeData);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      }
    };
    loadProjectType()
  }, []);

  // Validate date range
  const validateDateRange = (startDate: string, endDate: string): boolean => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      if (end < start) {
        setDateError('Project End Date must be greater than or equal to Project Start Date');
        return false;
      }
    }
    setDateError('');
    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'selectedCustomer') {
      setSelectedCustomerId(value);
    } else {
      const updatedProject = {
        ...project,
        [name]: value,
      };

      // Validate dates when either date field changes
      if (name === 'start_date' || name === 'end_date') {
        const startDate = name === 'start_date' ? value : project.start_date || '';
        const endDate = name === 'end_date' ? value : project.end_date || '';
        validateDateRange(startDate, endDate);
      }

      setProject(updatedProject);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (errorMessage) { return; }
    
    // Validate dates before submission
    if (!validateDateRange(project.start_date || '', project.end_date || '')) {
      return;
    }

    try {
      const projectWithCustomerId = {
        ...project,
        customer_id: selectedCustomerId || null,
      };

      await ProjectApi.create(projectWithCustomerId as Project);
      setMessage('Project created successfully');
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred.');
      }
    }
  };

  const handleCreateCustomer = () => {
    navigate('/main/customer/create');
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          <h1 className="text-2xl font-semibold text-foreground mb-6">Create Project</h1>

          {/* Success Message */}
          {message && (
            <Alert variant="success">
              {message}
            </Alert>
          )}

          {/* Error Message */}
          {errorMessage && (
            <Alert variant="error">
              {errorMessage}
            </Alert>
          )}

          {/* Date Validation Error */}
          {dateError && (
            <Alert variant="error">
              {dateError}
            </Alert>
          )}

          {/* No Customers Found */}
          {!message && customers.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-paragraph mb-6">
                No customer found. Before creating a project, you must have a customer created for the project.
                Click the Create Customer button to create a customer.
              </p>
              <Button onClick={handleCreateCustomer} size="medium" variant="default">
                <FilePlus className="size-5" />
                Create Customer
              </Button>
            </div>
          ) : !message && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Customer Selection */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                <h2 className="form-section-heading">Select Customer</h2>
                
                {selectedCustomerId && (
                  <p className="text-sm text-paragraph">
                    The selected customer is listed below. You can choose a different one if needed.
                  </p>
                )}

                <div>
                  <Label htmlFor="selectedCustomer">
                    Customer <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    id="selectedCustomer"
                    name="selectedCustomer"
                    value={selectedCustomerId}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  >
                    <option value="">Select Customer</option>
                    {customers.map((cust) => (
                      <option key={cust.customer_id} value={cust.customer_id}>
                        {cust.first_name} {cust.last_name} - {[
                          cust.address_1?.trim(),
                          cust.city?.trim(),
                          cust.state?.trim(),
                          cust.zip?.trim()
                        ].filter(Boolean).join(', ')}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Project Details */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                <h2 className="form-section-heading">Project Details</h2>

                <div>
                  <Label htmlFor="name">
                    Project Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={project.name || ''}
                    maxLength={50}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="project_description">
                    Project Description <span className="text-red-500">*</span> <span className="text-gray-500 text-xs">(Maximum 500 characters)</span>
                  </Label>
                  <Textarea
                    id="project_description"
                    name="project_description"
                    value={project.project_description || ''}
                    maxLength={500}
                    onChange={handleChange}
                    rows={2}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="project_type">
                    Project Type <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    id="project_type"
                    name="project_type"
                    value={project.project_type || ''}
                    onChange={handleChange}
                    required
                    className="w-full md:w-1/3 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  >
                    <option value="">Select a Project Type</option>
                    {projectTypeOptions.map((option, index) => (
                      <option key={index} value={option}>{option}</option>
                    ))}
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_date">Project Start Date</Label>
                    <Input
                      type="date"
                      id="start_date"
                      name="start_date"
                      value={project.start_date || ''}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <Label htmlFor="end_date">Project End Date</Label>
                    <Input
                      type="date"
                      id="end_date"
                      name="end_date"
                      value={project.end_date || ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <Button type="submit" size="medium" variant="default" disabled={!!dateError}>
                <FilePlus className="size-5" />
                Create Project
              </Button>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default CreateProjectPage;